import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Sprout, Plus, Pencil, Trash2, Wheat, Droplets, Scissors, MapPin, Map as MapIcon,
} from "lucide-react";
import { toast } from "sonner";
import PlotMap from "./crops/PlotMap";
import { farmDateIso } from "@/lib/farmTime";
import { useAuth } from "@/context/AuthContext";

// Common Philippine / SEA crop presets — free-form string, users can type anything
const CROP_PRESETS = [
    "Rice (Palay)", "Corn (Mais)", "Sugarcane", "Coconut", "Banana", "Cassava",
    "Sweet Potato (Kamote)", "Tomato", "Eggplant", "Okra", "Squash", "Bitter Gourd (Ampalaya)",
    "String Beans (Sitaw)", "Pechay", "Kangkong", "Onion", "Garlic", "Ginger",
    "Pineapple", "Mango", "Papaya", "Calamansi", "Coffee", "Cacao", "Peanut", "Mung Bean (Monggo)",
];

const STAGES = ["planted", "germination", "vegetative", "flowering", "fruiting", "mature", "harvested", "cleared"];
const STATUSES = ["active", "harvested", "cleared", "abandoned"];
const ACTIVITY_TYPES = [
    "land_prep", "planting", "fertilizer", "pesticide", "herbicide", "irrigation",
    "weeding", "pruning", "scouting", "labor", "other",
];

export default function CropsPage() {
    const { user } = useAuth();
    const role = String(user?.role_name || user?.role || "").toLowerCase().replace(/[_-]/g, " ");
    const canManage = ["owner", "admin", "manager", "farm manager", "crops supervisor"].includes(role) || Boolean(user?.role_perms?.all);
    const [summary, setSummary] = useState(null);
    const [plots, setPlots] = useState([]);
    const [activities, setActivities] = useState([]);
    const [harvests, setHarvests] = useState([]);
    const [dlg, setDlg] = useState(null);

    const load = useCallback(async () => {
        try {
            const [s, p, a, h] = await Promise.all([
                api.get("/crops/summary").then(r => r.data),
                api.get("/crop-plots").then(r => r.data),
                api.get("/crop-activities").then(r => r.data),
                api.get("/crop-harvests").then(r => r.data),
            ]);
            setSummary(s); setPlots(p || []); setActivities(a || []); setHarvests(h || []);
        } catch (err) { toast.error("Failed to load crop data"); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (path, body) => {
        try {
            if (body.id) { const { id, ...rest } = body; await api.put(`/${path}/${id}`, rest); }
            else await api.post(`/${path}`, body);
            toast.success("Saved"); setDlg(null); load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    };
    const del = async (path, id, label) => {
        if (!window.confirm(`Delete ${label}?`)) return;
        try { await api.delete(`/${path}/${id}`); toast.success("Deleted"); load(); }
        catch (err) { toast.error("Delete failed"); }
    };

    const plotMap = useMemo(() => Object.fromEntries(plots.map(p => [p.id, p])), [plots]);

    if (!summary) return <div className="p-6 text-muted-foreground">{i18n.t("final.33ce417454bf")}</div>;
    const k = summary.kpis;

    return (
        <div className="space-y-6" data-testid="crops-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="tiny-label flex items-center gap-2"><Sprout className="w-4 h-4" />{i18n.t("final.0fde937b9081")}</div>
                    <h1 className="text-3xl font-bold mt-1">{i18n.t("final.d4acbd528026")}</h1>
                    <p className="text-sm text-muted-foreground">{i18n.t("final.afdec0018746")}</p>
                </div>
                {canManage && <Button onClick={() => setDlg({ kind: "plot", data: {} })} data-testid="new-plot-btn">
                    <Plus className="w-4 h-4 mr-1" />{i18n.t("final.7a4af421292d")}
                </Button>}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                <Kpi label={i18n.t("final.04086c201fe3")} value={k.active_plots} testid="crops-kpi-active" />
                <Kpi label={i18n.t("final.1e02127a4af1")} value={k.total_plots} testid="crops-kpi-total-plots" />
                <Kpi label={i18n.t("final.948bdc50fc2a")} value={k.harvests_count} testid="crops-kpi-harvests" />
                <Kpi label={i18n.t("final.34a79d015463")} value={k.total_yield_kg} testid="crops-kpi-yield" />
                <Kpi label={i18n.t("final.35cf82f9d2a9")} value={formatCurrency(k.total_revenue)} raw testid="crops-kpi-revenue" />
                <Kpi label={i18n.t("final.64ae43e8fe76")} value={formatCurrency(k.total_cost)} raw testid="crops-kpi-cost" />
                <Kpi label={i18n.t("final.8544a4772583")} value={formatCurrency(k.profit)} raw tone={k.profit < 0 ? "red" : "green"} testid="crops-kpi-profit" />
            </div>

            <Tabs defaultValue="plots">
                <TabsList data-testid="crops-tabs">
                    <TabsTrigger value="plots" data-testid="crops-tab-plots"><MapPin className="w-4 h-4 mr-1" />{i18n.t("final.9c7f732592c6")}</TabsTrigger>
                    <TabsTrigger value="map" data-testid="crops-tab-map"><MapIcon className="w-4 h-4 mr-1" />{i18n.t("final.ab478f3efc84")}</TabsTrigger>
                    <TabsTrigger value="activities" data-testid="crops-tab-activities"><Droplets className="w-4 h-4 mr-1" />{i18n.t("final.e58f7f889902")}</TabsTrigger>
                    <TabsTrigger value="harvests" data-testid="crops-tab-harvests"><Wheat className="w-4 h-4 mr-1" />{i18n.t("final.948bdc50fc2a")}</TabsTrigger>
                </TabsList>

                {/* -------- PLOTS -------- */}
                <TabsContent value="plots" className="space-y-3">
                    <div className="space-y-3 md:hidden">
                        {plots.length === 0 && <Card className="p-6 text-center text-muted-foreground">
                            {i18n.t("final.9a14702e37fb")} {canManage && <Button variant="link" onClick={() => setDlg({ kind: "plot", data: {} })}>{i18n.t("final.61b1942702f8")}</Button>}
                        </Card>}
                        {plots.map((p) => (
                            <Card key={p.id} className="p-4 space-y-3" data-testid={`plot-mobile-${p.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div><div className="font-medium">{p.name || "—"}</div><div className="text-sm text-muted-foreground">{p.crop || "—"}</div></div>
                                    <Badge variant={p.status === "active" ? "default" : "secondary"} className="capitalize text-xs">{p.status || "active"}</Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div><span className="text-muted-foreground">{i18n.t("final.8fe9db16be51")}</span> {p.area_ha ? formatNumber(p.area_ha) : "—"} {i18n.t("final.f9fc27b9374a")}</div>
                                    <div><span className="text-muted-foreground">{i18n.t("final.41fbe295b7ab")}</span> {p.stage || "planted"}</div>
                                    <div><span className="text-muted-foreground">{i18n.t("final.b077b9af222e")}</span> {p.planted_date || "—"}</div>
                                    <div><span className="text-muted-foreground">{i18n.t("final.fd25edf497e5")}</span> {p.expected_harvest_date || "—"}</div>
                                </div>
                                {canManage && <div className="grid grid-cols-4 gap-1 border-t pt-2">
                                    <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "activity", data: { plot_id: p.id } })} data-testid={`mobile-log-activity-${p.id}`} aria-label={i18n.t("final.9b761dd12960")}><Droplets className="w-3.5 h-3.5" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "harvest", data: { plot_id: p.id } })} data-testid={`mobile-log-harvest-${p.id}`} aria-label={i18n.t("final.701c3a2750eb")}><Wheat className="w-3.5 h-3.5" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "plot", data: p })} data-testid={`mobile-edit-plot-${p.id}`} aria-label={i18n.t("final.1a56b9e8f6db")}><Pencil className="w-3.5 h-3.5" /></Button>
                                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del("crop-plots", p.id, p.name)} aria-label={i18n.t("final.5c9c59bd74d3")}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>}
                            </Card>
                        ))}
                    </div>
                    <Card className="hidden overflow-hidden md:block">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50"><tr className="text-left">
                                <Th>{i18n.t("final.36d4b773518b")}</Th><Th>{i18n.t("final.c5db3d91c4cb")}</Th><Th>{i18n.t("final.de8dc3b5d39f")}</Th><Th>{i18n.t("final.58caebadd832")}</Th><Th>{i18n.t("final.da2be8a737a6")}</Th><Th>{i18n.t("final.ca6d0e3aaa7d")}</Th><Th>{i18n.t("final.bae7d5be7082")}</Th><Th></Th>
                            </tr></thead>
                            <tbody>
                                {plots.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-8">
                                    {i18n.t("final.9a14702e37fb")} <Button variant="link" onClick={() => setDlg({ kind: "plot", data: {} })}>{i18n.t("final.61b1942702f8")}</Button>
                                </td></tr>}
                                {plots.map((p) => (
                                    <tr key={p.id} className="border-t" data-testid={`plot-row-${p.id}`}>
                                        <td className="p-2 font-medium">{p.name || "—"}</td>
                                        <td className="p-2">{p.crop || "—"}</td>
                                        <td className="p-2">{p.area_ha ? formatNumber(p.area_ha) : "—"}</td>
                                        <td className="p-2 text-xs">{p.planted_date || "—"}</td>
                                        <td className="p-2 text-xs">{p.expected_harvest_date || "—"}</td>
                                        <td className="p-2"><Badge variant="outline" className="capitalize text-xs">{p.stage || "planted"}</Badge></td>
                                        <td className="p-2"><Badge variant={p.status === "active" ? "default" : "secondary"} className="capitalize text-xs">{p.status || "active"}</Badge></td>
                                        <td className="p-2 text-right whitespace-nowrap">
                                            {canManage && <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "activity", data: { plot_id: p.id } })} data-testid={`log-activity-${p.id}`}><Droplets className="w-3.5 h-3.5" /></Button>}
                                            {canManage && <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "harvest", data: { plot_id: p.id } })} data-testid={`log-harvest-${p.id}`}><Wheat className="w-3.5 h-3.5" /></Button>}
                                            {canManage && <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "plot", data: p })} data-testid={`edit-plot-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                                            {canManage && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del("crop-plots", p.id, p.name)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                {/* -------- MAP -------- */}
                <TabsContent value="map" className="space-y-3">
                    <Card className="p-3">
                        <div className="text-xs text-muted-foreground mb-2">
                            {plots.filter(p => p.lat && p.lng).length} {i18n.t("final.de04fa0e29f9")} {plots.length} {i18n.t("final.c010b1f6ac90")}
                        </div>
                        <PlotMap plots={plots} height={500} />
                    </Card>
                </TabsContent>

                {/* -------- ACTIVITIES -------- */}
                <TabsContent value="activities" className="space-y-3">
                    <div className="flex justify-end">
                        {canManage && <Button onClick={() => setDlg({ kind: "activity", data: {} })} data-testid="new-activity-btn"><Plus className="w-4 h-4 mr-1" />{i18n.t("final.5af2ae528662")}</Button>}
                    </div>
                    <Card className="overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50"><tr className="text-left">
                                <Th>{i18n.t("final.eb9a4bc1c0c1")}</Th><Th>{i18n.t("final.36d4b773518b")}</Th><Th>{i18n.t("final.3deb74565196")}</Th><Th>{i18n.t("final.b568d47f2e24")}</Th><Th>{i18n.t("final.1e5ff9e500c2")}</Th><Th>{i18n.t("final.64ae43e8fe76")}</Th><Th></Th>
                            </tr></thead>
                            <tbody>
                                {activities.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-6">{i18n.t("final.bbc7f274b22b")}</td></tr>}
                                {activities.slice().sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((a) => (
                                    <tr key={a.id} className="border-t" data-testid={`activity-row-${a.id}`}>
                                        <td className="p-2 text-xs">{a.date || "—"}</td>
                                        <td className="p-2">{plotMap[a.plot_id]?.name || "—"}</td>
                                        <td className="p-2 capitalize">{(a.activity_type || "other").replace(/_/g, " ")}</td>
                                        <td className="p-2">{a.input_name || "—"}</td>
                                        <td className="p-2">{a.quantity ? `${formatNumber(a.quantity)} ${a.unit || ""}` : "—"}</td>
                                        <td className="p-2">{a.cost ? formatCurrency(a.cost) : "—"}</td>
                                        <td className="p-2 text-right whitespace-nowrap">
                                            {canManage && <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "activity", data: a })}><Pencil className="w-3.5 h-3.5" /></Button>}
                                            {canManage && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del("crop-activities", a.id, "activity")}><Trash2 className="w-3.5 h-3.5" /></Button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                {/* -------- HARVESTS -------- */}
                <TabsContent value="harvests" className="space-y-3">
                    <div className="flex justify-end">
                        {canManage && <Button onClick={() => setDlg({ kind: "harvest", data: {} })} data-testid="new-harvest-btn"><Plus className="w-4 h-4 mr-1" />{i18n.t("final.0bf662f2c2f6")}</Button>}
                    </div>
                    <Card className="overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50"><tr className="text-left">
                                <Th>{i18n.t("final.eb9a4bc1c0c1")}</Th><Th>{i18n.t("final.36d4b773518b")}</Th><Th>{i18n.t("final.c5db3d91c4cb")}</Th><Th>{i18n.t("final.34a79d015463")}</Th><Th>{i18n.t("final.aecc49c15355")}</Th><Th>{i18n.t("final.35cf82f9d2a9")}</Th><Th></Th>
                            </tr></thead>
                            <tbody>
                                {harvests.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-6">{i18n.t("final.231ce14decd0")}</td></tr>}
                                {harvests.slice().sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((h) => (
                                    <tr key={h.id} className="border-t" data-testid={`harvest-row-${h.id}`}>
                                        <td className="p-2 text-xs">{h.date || "—"}</td>
                                        <td className="p-2">{plotMap[h.plot_id]?.name || "—"}</td>
                                        <td className="p-2">{h.crop || plotMap[h.plot_id]?.crop || "—"}</td>
                                        <td className="p-2">{h.yield_kg ? formatNumber(h.yield_kg) : "—"}</td>
                                        <td className="p-2 capitalize">{h.grade || "—"}</td>
                                        <td className="p-2 font-medium">{h.revenue ? formatCurrency(h.revenue) : "—"}</td>
                                        <td className="p-2 text-right whitespace-nowrap">
                                            {canManage && <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "harvest", data: h })}><Pencil className="w-3.5 h-3.5" /></Button>}
                                            {canManage && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del("crop-harvests", h.id, "harvest")}><Trash2 className="w-3.5 h-3.5" /></Button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>
            </Tabs>

            {dlg?.kind === "plot" && <PlotDialog initial={dlg.data} onSave={(b) => save("crop-plots", b)} onClose={() => setDlg(null)} />}
            {dlg?.kind === "activity" && <ActivityDialog initial={dlg.data} plots={plots} onSave={(b) => save("crop-activities", b)} onClose={() => setDlg(null)} />}
            {dlg?.kind === "harvest" && <HarvestDialog initial={dlg.data} plots={plots} onSave={(b) => save("crop-harvests", b)} onClose={() => setDlg(null)} />}
        </div>
    );
}

function Kpi({ label, value, sub, testid, tone = "slate", raw = false }) {
    const cls =
        tone === "red" ? "bg-destructive/10 border-destructive/40 text-destructive" :
        tone === "green" ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-400" :
        "bg-secondary border-border";
    return (
        <Card className={`p-3 border ${cls}`} data-testid={testid}>
            <div className="tiny-label">{label}</div>
            <div className="text-2xl font-bold mt-1">{raw ? value : formatNumber(value || 0)}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </Card>
    );
}
function Th({ children }) { return <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">{children}</th>; }
function Field({ label, children }) { return <div><div className="text-xs text-muted-foreground mb-1">{label}</div>{children}</div>; }

function PlotDialog({ initial, onSave, onClose }) {
    const [f, setF] = useState({
        name: "", crop: "", area_ha: "", location: "", lat: null, lng: null,
        planted_date: farmDateIso(),
        expected_harvest_date: "", stage: "planted", status: "active",
        seed_variety: "", seed_source: "", notes: "", ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    const pin = (f.lat && f.lng) ? [Number(f.lat), Number(f.lng)] : null;
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-2xl" data-testid="plot-dialog">
            <DialogHeader><DialogTitle>{f.id ? "Edit Plot" : "New Crop Plot"}</DialogTitle><DialogDescription>{i18n.t("final.12cf8c715664")}</DialogDescription></DialogHeader>
            <div className="grid md:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto pr-1">
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <Field label={i18n.t("final.582c2c765413")}><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="plot-name-input" placeholder={i18n.t("final.e2767823d537")} /></Field>
                        <Field label={i18n.t("final.3f0631d97412")}><Input type="number" step="0.01" value={f.area_ha} onChange={(e) => set("area_ha", e.target.value)} placeholder="1.5" /></Field>
                    </div>
                    <Field label={i18n.t("final.19b818028820")}>
                        <Input value={f.crop} onChange={(e) => set("crop", e.target.value)} data-testid="plot-crop-input" list="crop-presets" placeholder={i18n.t("final.e623de0e43ee")} />
                        <datalist id="crop-presets">
                            {CROP_PRESETS.map((c) => <option key={c} value={c} />)}
                        </datalist>
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label={i18n.t("final.d8d4986afb9d")}><Input value={f.seed_variety} onChange={(e) => set("seed_variety", e.target.value)} placeholder={i18n.t("final.fe88fffd8a1e")} /></Field>
                        <Field label={i18n.t("final.17d809d2e31a")}><Input value={f.seed_source} onChange={(e) => set("seed_source", e.target.value)} placeholder={i18n.t("final.55edd462872a")} /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label={i18n.t("final.58caebadd832")}><Input type="date" value={f.planted_date} onChange={(e) => set("planted_date", e.target.value)} data-testid="plot-planted-input" /></Field>
                        <Field label={i18n.t("final.108839a6a9c6")}><Input type="date" value={f.expected_harvest_date} onChange={(e) => set("expected_harvest_date", e.target.value)} /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label={i18n.t("final.ca6d0e3aaa7d")}>
                            <Select value={f.stage} onValueChange={(v) => set("stage", v)}>
                                <SelectTrigger data-testid="plot-stage-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label={i18n.t("final.bae7d5be7082")}>
                            <Select value={f.status} onValueChange={(v) => set("status", v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <Field label={i18n.t("final.a69606c0dc7b")}><Input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder={i18n.t("final.70f90c2aa98d")} /></Field>
                    <Field label={i18n.t("final.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
                </div>

                <div className="space-y-2" data-testid="plot-map-picker">
                    <div className="text-xs text-muted-foreground">{i18n.t("final.ef46a8d2a13d")}</div>
                    <PlotMap
                        pickable
                        center={pin}
                        zoom={pin ? 15 : 6}
                        height={300}
                        onPick={(lat, lng) => setF((cur) => ({ ...cur, lat, lng }))}
                    />
                    <div className="grid grid-cols-3 gap-2 items-end">
                        <Field label={i18n.t("final.ced3905ec822")}>
                            <Input type="number" step="0.000001" value={f.lat ?? ""} onChange={(e) => set("lat", e.target.value ? Number(e.target.value) : null)} data-testid="plot-lat-input" />
                        </Field>
                        <Field label={i18n.t("final.6f9f011c5d36")}>
                            <Input type="number" step="0.000001" value={f.lng ?? ""} onChange={(e) => set("lng", e.target.value ? Number(e.target.value) : null)} data-testid="plot-lng-input" />
                        </Field>
                        <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => setF((cur) => ({ ...cur, lat: null, lng: null }))}
                            data-testid="clear-gps-btn"
                        >{i18n.t("final.94db78855af5")}</Button>
                    </div>
                </div>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                <Button onClick={() => (f.name.trim() && f.crop.trim()) ? onSave({ ...f, area_ha: Number(f.area_ha) || 0 }) : toast.error("Name and crop required")} data-testid="save-plot-btn">{i18n.t("final.efc007a393f6")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}

function ActivityDialog({ initial, plots, onSave, onClose }) {
    const [f, setF] = useState({
        plot_id: "", activity_type: "fertilizer", date: farmDateIso(),
        input_name: "", quantity: "", unit: "kg", cost: "", notes: "", ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="activity-dialog">
            <DialogHeader><DialogTitle>{f.id ? "Edit Activity" : "Log Crop Activity"}</DialogTitle><DialogDescription>{i18n.t("final.170c36264a9c")}</DialogDescription></DialogHeader>
            <div className="space-y-3">
                <Field label={i18n.t("final.a4f83557c6d5")}>
                    <Select value={f.plot_id} onValueChange={(v) => set("plot_id", v)}>
                        <SelectTrigger data-testid="activity-plot-select"><SelectValue placeholder={i18n.t("final.4496cd8c9d41")} /></SelectTrigger>
                        <SelectContent>{plots.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.crop}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("final.3deb74565196")}>
                        <Select value={f.activity_type} onValueChange={(v) => set("activity_type", v)}>
                            <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{ACTIVITY_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                    <Field label={i18n.t("final.eb9a4bc1c0c1")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Field>
                </div>
                <Field label={i18n.t("final.ae61226fd3ab")}><Input value={f.input_name} onChange={(e) => set("input_name", e.target.value)} placeholder={i18n.t("final.4011820e918a")} /></Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("final.44f6af694554")}><Input type="number" step="0.01" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} /></Field>
                    <Field label={i18n.t("final.f6b935ab3371")}><Input value={f.unit} onChange={(e) => set("unit", e.target.value)} placeholder={i18n.t("final.0e48c7de4a3f")} /></Field>
                </div>
                <Field label={i18n.t("final.b13bdb89caf8")}><Input type="number" step="0.01" value={f.cost} onChange={(e) => set("cost", e.target.value)} data-testid="activity-cost-input" /></Field>
                <Field label={i18n.t("final.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                <Button onClick={() => f.plot_id ? onSave({ ...f, quantity: Number(f.quantity) || 0, cost: Number(f.cost) || 0 }) : toast.error("Plot required")} data-testid="save-activity-btn">{i18n.t("final.efc007a393f6")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}

function HarvestDialog({ initial, plots, onSave, onClose }) {
    const [f, setF] = useState({
        plot_id: "", crop: "", date: farmDateIso(),
        yield_kg: "", grade: "A", price_per_kg: "", revenue: "", buyer: "", notes: "", ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    const autoRevenue = (kg, pkg) => {
        const y = Number(kg) || 0; const p = Number(pkg) || 0;
        return y * p ? (y * p).toFixed(2) : "";
    };
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="harvest-dialog">
            <DialogHeader><DialogTitle>{f.id ? "Edit Harvest" : "Log Harvest"}</DialogTitle><DialogDescription>{i18n.t("final.e262a962cde2")}</DialogDescription></DialogHeader>
            <div className="space-y-3">
                <Field label={i18n.t("final.a4f83557c6d5")}>
                    <Select value={f.plot_id} onValueChange={(v) => { const p = plots.find(x => x.id === v); setF({ ...f, plot_id: v, crop: f.crop || p?.crop || "" }); }}>
                        <SelectTrigger data-testid="harvest-plot-select"><SelectValue placeholder={i18n.t("final.4496cd8c9d41")} /></SelectTrigger>
                        <SelectContent>{plots.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.crop}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("final.c5db3d91c4cb")}><Input value={f.crop} onChange={(e) => set("crop", e.target.value)} /></Field>
                    <Field label={i18n.t("final.eb9a4bc1c0c1")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Field>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <Field label={i18n.t("final.62b4e20b30bd")}><Input type="number" step="0.01" value={f.yield_kg} onChange={(e) => { const v = e.target.value; setF({ ...f, yield_kg: v, revenue: autoRevenue(v, f.price_per_kg) || f.revenue }); }} data-testid="harvest-yield-input" /></Field>
                    <Field label={i18n.t("final.b0854dcfffc4")}><Input type="number" step="0.01" value={f.price_per_kg} onChange={(e) => { const v = e.target.value; setF({ ...f, price_per_kg: v, revenue: autoRevenue(f.yield_kg, v) || f.revenue }); }} /></Field>
                    <Field label={i18n.t("final.501e80194351")}><Input type="number" step="0.01" value={f.revenue} onChange={(e) => set("revenue", e.target.value)} data-testid="harvest-revenue-input" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("final.aecc49c15355")}>
                        <Select value={f.grade} onValueChange={(v) => set("grade", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{["A", "B", "C", "mixed", "reject"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                    <Field label={i18n.t("final.4186c93a90f9")}><Input value={f.buyer} onChange={(e) => set("buyer", e.target.value)} placeholder={i18n.t("final.0c6c4102d4df")} /></Field>
                </div>
                <Field label={i18n.t("final.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                <Button onClick={() => (f.plot_id && (Number(f.yield_kg) > 0)) ? onSave({ ...f, yield_kg: Number(f.yield_kg) || 0, price_per_kg: Number(f.price_per_kg) || 0, revenue: Number(f.revenue) || 0 }) : toast.error("Plot and yield required")} data-testid="save-harvest-btn">{i18n.t("final.efc007a393f6")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}
